export interface LogEntry {
  id: string;
  timestamp: Date;
  type: 'log' | 'error' | 'warn' | 'info' | 'debug';
  message: string;
  data?: any[];
  stack?: string;
}

class ConsoleLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private isCapturing = true;

  private originalConsole: {
    log: typeof console.log;
    error: typeof console.error;
    warn: typeof console.warn;
    info: typeof console.info;
    debug: typeof console.debug;
    table: typeof console.table;
  };

  constructor() {
    this.originalConsole = {
      log: console.log.bind(console),
      error: console.error.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
      table: console.table?.bind(console) || (() => {}),
    };

    this.startCapture();
  }

  private startCapture() {
    console.log = (...args: any[]) => {
      this.addLog('log', args);
      this.originalConsole.log(...args);
    };

    console.error = (...args: any[]) => {
      this.addLog('error', args);
      this.originalConsole.error(...args);
    };

    console.warn = (...args: any[]) => {
      this.addLog('warn', args);
      this.originalConsole.warn(...args);
    };

    console.info = (...args: any[]) => {
      this.addLog('info', args);
      this.originalConsole.info(...args);
    };

    console.debug = (...args: any[]) => {
      this.addLog('debug', args);
      this.originalConsole.debug(...args);
    };
  }

  private addLog(type: LogEntry['type'], data: any[]) {
    if (!this.isCapturing) return;

    const message = data
      .map((item) => {
        if (typeof item === 'object') {
          try {
            return JSON.stringify(item, null, 2);
          } catch {
            return String(item);
          }
        }
        return String(item);
      })
      .join(' ');

    const entry: LogEntry = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      type,
      message,
      data,
    };

    this.logs.push(entry);

    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  public getLogs(): LogEntry[] {
    return [...this.logs];
  }

  public clearLogs() {
    this.logs = [];
  }

  public stopCapture() {
    this.isCapturing = false;
  }

  public resumeCapture() {
    this.isCapturing = true;
  }

  public restore() {
    console.log = this.originalConsole.log;
    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;
  }

  public getStats() {
    const byType = {
      log: 0,
      error: 0,
      warn: 0,
      info: 0,
      debug: 0,
    };

    this.logs.forEach((log) => {
      byType[log.type]++;
    });

    return {
      total: this.logs.length,
      byType,
    };
  }

  public downloadLogs() {
    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `console-logs-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  public downloadLogsAsText() {
    const textContent = this.logs
      .map((log) => {
        const timestamp = log.timestamp.toISOString();
        return `[${timestamp}] [${log.type.toUpperCase()}] ${log.message}`;
      })
      .join('\n');

    const dataBlob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `console-logs-${new Date().toISOString()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export const consoleLogger = new ConsoleLogger();

export default consoleLogger;
